import type { PromotionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/promotion-type.enum'
import type { Promotion as TsClientPromotionDto } from '@empathybroker/tsclient-play/dist/models/promotion'
import type {
  ConflictingElementInfo,
  CrudServiceListResponse,
  Identifiable,
  Pivot,
  Promotion,
  PromotionAdapter,
  PromotionDTO,
} from '@empathyco/playboard-core-domain'
import type { PlayServiceCrudWrapper } from '../../../services/interfaces/crud'
import type { PromotionFactory } from '../domain/types'
import type { PromotionCrudService, PromotionCrudServiceListParams } from './types'
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import { AdapterDateUtils, ConflictApiError } from '@empathyco/playboard-core-domain'
import { inject, injectable, named } from 'inversify'
import { TYPES } from '../../../config/inversify.types'
import { FeatureIdEnum } from '../../../enums/feature-id.enum'

export type PlatformPromotionEntity = TsClientPromotionDto &
  Identifiable & {
    meta?: {
      creationDate: Date
      updateDate: Date
      user: string
    }
  }

interface ConflictingPromotionElementInfo extends ConflictingElementInfo {
  type?: PromotionTypeEnum
}

@injectable()
export class PlatformPromotionCrudService implements PromotionCrudService {
  private feature = ActionTypeEnum.PROMOTION

  public constructor(
    @named(FeatureIdEnum.PROMOTION)
    @inject(TYPES.PromotionAdapter)
    protected adapter: PromotionAdapter<PlatformPromotionEntity>,
    @inject(TYPES.PlayServiceCrudWrapper)
    protected playWrapperService: PlayServiceCrudWrapper<PlatformPromotionEntity>,
    @inject(TYPES.PromotionFactory)
    protected factory: PromotionFactory,
  ) {}

  async list(params: PromotionCrudServiceListParams): Promise<CrudServiceListResponse<Promotion>> {
    try {
      const response = await this.playWrapperService.list(this.feature, params)

      return {
        numFound: response.numFound,
        results: response.results.map(promotionEntity => {
          const promotionDTO: PromotionDTO = this.adapter.decode(promotionEntity)
          return this.factory(promotionDTO)
        }),
      }
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async create(promotion: Promotion): Promise<Promotion> {
    try {
      const overlappingError = await this.getOverlappingPromotionsError(promotion)
      if (overlappingError) {
        throw overlappingError
      }
      const promotionEntity = this.adapter.encode(promotion)
      const response = await this.playWrapperService.create(this.feature, promotionEntity)
      const promotionDTO: PromotionDTO = this.adapter.decode(response)
      return this.factory(promotionDTO)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async read(id: Promotion['id']): Promise<Promotion> {
    try {
      const response = await this.playWrapperService.read(this.feature, id as unknown as string)
      const promotionDTO: PromotionDTO = this.adapter.decode(response)
      return this.factory(promotionDTO)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async update(promotion: Promotion): Promise<Promotion> {
    try {
      const overlappingError = await this.getOverlappingPromotionsError(promotion)
      if (overlappingError) {
        throw overlappingError
      }
      const promotionEntity = await this.playWrapperService.update(
        this.feature,
        this.adapter.encode(promotion),
      )
      const promotionDTO: PromotionDTO = this.adapter.decode(promotionEntity)
      return this.factory(promotionDTO)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  async delete(promotion: Promotion): Promise<void> {
    try {
      await this.playWrapperService.delete(this.feature, promotion.id as unknown as string)
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private getPivotsValues = (pivots: Pivot[]): Record<string, string[]> => {
    const nonEmptyPivots = pivots.filter(pivot => pivot.values.length)
    return nonEmptyPivots.reduce((pivotsObject: Record<string, string[]>, { name, values }) => {
      pivotsObject[name] = values
      return pivotsObject
    }, {})
  }

  private async getOverlappingPromotions(
    promotion: Promotion,
  ): Promise<CrudServiceListResponse<Promotion>> {
    if (!promotion.criteria.enabled) {
      return {
        results: [],
        numFound: 0,
      }
    }
    const promotionsSearchResult = await this.list({
      filters: {
        ...this.getPivotsValues(promotion.criteria.pivots),
        enabled: promotion.criteria.enabled,
      },
      extraParameters: {
        position: promotion.setting.position,
        type: promotion.setting.type as PromotionTypeEnum,
        ...(promotion.criteria.matchAll ? {} : { keyword: promotion.criteria.queries }),
        start_date: AdapterDateUtils.getDateWithoutMilliseconds(
          promotion.schedule.activationDate || new Date(),
        ),
        ...(promotion.schedule?.deactivationDate && {
          end_date: AdapterDateUtils.getDateWithoutMilliseconds(
            promotion.schedule.deactivationDate,
          ),
        }),
      },
    })

    const isExistingPromotion =
      !!promotion.id && promotionsSearchResult.results.find(({ id }) => id === promotion.id)

    return isExistingPromotion
      ? {
          results: promotionsSearchResult.results.filter(({ id }) => id !== promotion.id),
          numFound: promotionsSearchResult.numFound - 1,
        }
      : promotionsSearchResult
  }

  private async getOverlappingPromotionsError(
    promotion: Promotion,
  ): Promise<ConflictApiError | null> {
    const { results: overlappingPromotions, numFound: totalOverlappingPromotions } =
      await this.getOverlappingPromotions(promotion)

    if (totalOverlappingPromotions === 0) {
      return null
    }

    return new ConflictApiError({
      conflictingElements: overlappingPromotions.map((conflictingPromotion: Promotion) => {
        return {
          id: conflictingPromotion.id,
          name: conflictingPromotion.setting.title,
          queries: conflictingPromotion.criteria.queries,
          type: conflictingPromotion.setting.type,
        } as ConflictingPromotionElementInfo
      }),
    })
  }
}
